/* ==========================================================================
   COOKIE CARD - Shopify Customer Privacy API wiring.

   WHAT THIS REPLACES: Shopify's own cookie banner. That banner MUST be turned
   off in Settings > Customer privacy, or both will appear. This file does not
   and cannot turn it off; there is no API for it.

   THE CONTRACT, from Shopify's docs, and each line here exists because getting
   it wrong fails silently:
     1. window.Shopify.loadFeatures(['consent-tracking-api']) must RESOLVE
        before window.Shopify.customerPrivacy exists. Reading it earlier gives
        undefined, and the card then never shows.
     2. shouldShowBanner() is the region check. It already knows which visitors
        legally need to be asked, so we never decide that ourselves.
     3. setTrackingConsent is only ever called from a click handler. Consent
        that the visitor did not click is not consent.
     4. sale_of_data is NOT set here. Shopify treats it as an opt-out handled by
        its own data-sale page, and writing it from a generic accept would
        misrepresent a US opt-out as an EU opt-in.

   GRANULARITY: analytics, marketing and preferences are set INDEPENDENTLY when
   the visitor opens Choose. UK and EU GDPR require consent to be specific per
   purpose, and Shopify's own banner offers that, so removing it would be a
   step backwards. Accept and Decline remain one-click paths for people who do
   not want to think about it.

   PAINTED BY LIQUID (P211, 7 Oct 2026): for UK and EU markets the card arrives
   in the HTML already visible (data-cc-pre), so the buttons are wired at once
   and a click made before the API has loaded is held and recorded when it
   lands; the card still only closes on a clean write. Shopify keeps the answer
   server-side in an httpOnly cookie (measured), so once an answer is recorded
   or known this writes localStorage stk_cc, which theme.liquid reads in the
   head to hide the card before it can flash. Storing the visitor's own consent
   choice is strictly necessary storage. If the flag and Shopify disagree,
   Shopify wins and the card comes back, with one measured exception: the first
   page after an answer (see the lag note below), where this browser's own record
   of the click is trusted instead.

   NO DARK PATTERNS: Accept and Decline are the same size, same weight, same
   colour. The page behind stays scrollable and usable. If a later pass wants
   Accept to stand out, the answer is no.
   ========================================================================== */
(function () {
  'use strict';

  var card = document.querySelector('[data-cc]');
  if (!card) return;

  var panel = card.querySelector('[data-cc-panel]');
  var errLine = card.querySelector('[data-cc-err]');
  var pre = card.hasAttribute('data-cc-pre');
  var privacy = null;
  var failed = false;
  var pending = null;
  var timer = 0;
  var lastFocus = null;
  /* The API normally lands within 15 s even on Lighthouse's slow phone; past
     this it is treated as blocked (fail). A late arrival still works. */
  var WAIT = 20000;
  var root = document.documentElement;

  /* 22 Oct 2026: Shopify's own record lags one page behind a new answer, so
     visitors saw the card twice. MEASURED on live (11/11 runs, and again here):
     the first page requested after Accept or Decline bounces
     stickiply.com -> shop.app/accounts/sur -> back to the same URL carrying
     ?_su_rec=...; that landing page reads currentVisitorConsent() as all "",
     whatever the delay (idle 11 minutes and it still bounced). The page after
     reads the answer. So the lag belongs to that one bounced page, not to a
     period of time: a click recorded in this browser stores its time ('t' + ms),
     and a Shopify "unanswered" is taken as the lag while either the stamp is
     fresh OR this page is the bounce landing. Once Shopify confirms the answer
     the stamp becomes a plain '1' and Shopify wins again from then on. */
  var FRESH = 10 * 60 * 1000;

  /* fresh: the visitor's click was just recorded. A known answer read from
     Shopify keeps any flag already there, so browsing never renews FRESH. */
  function mark(on, fresh) {
    try {
      if (!on) localStorage.removeItem('stk_cc');
      else if (fresh) localStorage.setItem('stk_cc', 't' + Date.now());
      else if (!localStorage.getItem('stk_cc')) localStorage.setItem('stk_cc', '1');
    } catch (e) {}
  }

  function justAnswered() {
    var f = null;
    try { f = localStorage.getItem('stk_cc'); } catch (e) {}
    if (!f || f.charAt(0) !== 't') return false;
    var age = Date.now() - parseInt(f.slice(1), 10);
    if (age >= 0 && age < FRESH) return true;
    /* the bounce landing itself: ?_su_rec= is put there by shop.app on the first
       page after an answer, and typing it by hand does not blank consent */
    return /[?&]_su_rec=/.test(location.search);
  }

  function setErr(on) {
    if (errLine) errLine.hidden = !on;
  }

  /* The close in progress, if any. show() clears it so a close that was still
     fading (an auto-close, say) cannot hide a card the visitor just reopened. */
  var closing = null;

  function show() {
    closing = null;
    root.classList.remove('cc-done');
    if (!card.contains(document.activeElement)) lastFocus = document.activeElement;
    card.hidden = false;
    /* next frame so the transition has a start state to move from */
    requestAnimationFrame(function () {
      card.classList.add('is-in');
      card.focus();
    });
  }

  function hide() {
    /* Focus goes back only if the card holds it: a card closing on its own
       must never pull focus out of a field the visitor is typing in. */
    var had = card.contains(document.activeElement);
    var me = {};
    closing = me;
    card.classList.remove('is-in');
    var done = function () {
      card.removeEventListener('transitionend', done);
      if (closing !== me) return;
      closing = null;
      card.hidden = true;
      if (!had) return;
      had = false;
      /* body cannot take focus, so fall back to the main landmark rather than
         letting the next Tab start in the footer. */
      var to = lastFocus && lastFocus !== document.body ? lastFocus : document.getElementById('MainContent');
      if (to && to.focus) to.focus({ preventScroll: true });
    };
    card.addEventListener('transitionend', done);
    /* Under prefers-reduced-motion the transition is ~0ms, so transitionend can
       fire before the listener attaches. This backstop closes it either way. */
    setTimeout(done, 400);
  }

  /* Only a clean write closes the card. A choice that was not recorded must
     never look like one that was. */
  function record(consent) {
    setErr(false);
    if (!privacy) {
      /* Painted before the API loaded: hold the latest click until it does.
         The buttons show busy (CSS); if the API is past WAIT the error line
         says so, and a late API still records this click. */
      pending = consent;
      if (failed) setErr(true);
      else card.setAttribute('aria-busy', 'true');
      return;
    }
    try {
      privacy.setTrackingConsent(consent, function (result) {
        if (result && result.error) {
          setErr(true);
          return;
        }
        mark(true, true);
        hide();
      });
    } catch (e) {
      setErr(true);
    }
  }

  function all(granted) {
    return { analytics: granted, marketing: granted, preferences: granted };
  }

  /* Has this visitor already answered?

     shouldShowBanner() alone is NOT enough. Measured on the live storefront
     29 Aug 2026: after Accept, currentVisitorConsent() correctly returned
     {analytics:"yes", marketing:"yes", preferences:"yes"} and yet
     shouldShowBanner() still returned true on the next page load, so the card
     came back on every page for ever, for someone who had already answered.

     shouldShowBanner() answers "does this visitor's region require asking".
     Whether they have answered is a separate question, and this is it. Each
     value is "yes", "no" or "" - an empty string means undecided, and BOTH
     "yes" and "no" count as answered, so declining silences it too. */
  function hasAnswered() {
    if (typeof privacy.currentVisitorConsent !== 'function') return false;
    var c;
    try {
      c = privacy.currentVisitorConsent();
    } catch (e) {
      return false;
    }
    if (!c) return false;
    return !!(c.analytics || c.marketing || c.preferences);
  }

  function wire() {
    var accept = card.querySelector('[data-cc-accept]');
    var decline = card.querySelector('[data-cc-decline]');
    var choose = card.querySelector('[data-cc-choose]');
    var save = card.querySelector('[data-cc-save]');

    if (accept) accept.addEventListener('click', function () { record(all(true)); });
    if (decline) decline.addEventListener('click', function () { record(all(false)); });

    if (choose && panel) {
      choose.addEventListener('click', function () {
        drop();
        var open = panel.hidden;
        panel.hidden = !open;
        choose.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          var first = panel.querySelector('input');
          if (first) first.focus();
        }
      });
    }

    if (save && panel) {
      save.addEventListener('click', function () {
        var get = function (name) {
          var el = panel.querySelector('[data-cc-opt="' + name + '"]');
          return el ? !!el.checked : false;
        };
        record({
          analytics: get('analytics'),
          marketing: get('marketing'),
          preferences: get('preferences'),
        });
      });
    }

    /* Escape closes the preferences panel only. It must NOT dismiss the card:
       dismissing without a choice would leave consent unrecorded while looking
       resolved. */
    /* Changing a box means a held Accept or Decline is no longer the answer. */
    if (panel) panel.addEventListener('change', function () { dirty = true; drop(); });

    card.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !panel || panel.hidden) return;
      panel.hidden = true;
      if (choose) {
        choose.setAttribute('aria-expanded', 'false');
        choose.focus();
      }
    });
  }

  function drop() {
    pending = null;
    card.removeAttribute('aria-busy');
  }

  /* Closes without a recorded choice. Only for a visitor Shopify already has
     an answer for, or one whose consent API is blocked, where it could never
     record one; Shopify then keeps tracking off where consent is required. */
  function quietHide() {
    if (card.hidden) return;
    if (root.classList.contains('cc-done')) {
      card.hidden = true;
      return;
    }
    if (panel && !panel.hidden) return;
    hide();
  }

  /* Cookie settings (footer link, data-cc-open): withdrawing or changing consent
     must be as easy as giving it (UK/EU GDPR art. 7(3)). Reopens the card with
     Choose open and the visitor's current answers ticked, so changing one is
     one untick and Save. The stk_cc flag stays: they have answered, and if they
     leave without saving, Shopify still holds that answer. */
  var dirty = false;

  /* Ticks the boxes from Shopify's record. Before the API has loaded there is
     nothing to read, so ready() runs it again unless the visitor has already
     changed a box. */
  function tick() {
    if (!panel || !privacy || typeof privacy.currentVisitorConsent !== 'function') return;
    var c = {};
    try { c = privacy.currentVisitorConsent() || {}; } catch (x) {}
    ['analytics', 'marketing', 'preferences'].forEach(function (n) {
      var el = panel.querySelector('[data-cc-opt="' + n + '"]');
      if (el) el.checked = c[n] === 'yes';
    });
  }

  function reopen(e) {
    var a = e.target && e.target.closest ? e.target.closest('[data-cc-open]') : null;
    if (!a) return;
    /* A new-tab click still goes to the privacy policy. */
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
    e.preventDefault();
    drop();
    setErr(false);
    dirty = false;
    if (panel) {
      tick();
      panel.hidden = false;
      var choose = card.querySelector('[data-cc-choose]');
      if (choose) choose.setAttribute('aria-expanded', 'true');
    }
    show();
    /* Safari does not focus a clicked link, so name the way back explicitly. */
    lastFocus = a;
  }

  /* hard: the API reported an error or is missing, so it will never record;
     the card closes unless a click is held. Not hard: the WAIT watchdog, where
     the API may still be on its way, so only a held click is told. */
  function fail(hard) {
    if (privacy) return;
    failed = true;
    card.removeAttribute('aria-busy');
    if (pending) setErr(true);
    else if (hard && pre) quietHide();
  }

  function ready() {
    clearTimeout(timer);
    failed = false;
    var answered = hasAnswered();
    var ask = privacy.shouldShowBanner();
    var visible = !card.hidden && !root.classList.contains('cc-done');
    if (visible && panel && !panel.hidden && !dirty) tick();

    if (answered) {
      /* Shopify confirms it: replace a 't' stamp with the plain flag, so the
         allowance above ends here and Shopify wins on every later page. */
      try { var f = localStorage.getItem('stk_cc'); if (f && f.charAt(0) === 't') localStorage.setItem('stk_cc', '1'); } catch (e) {}
      mark(true);
      /* Answered before stk_cc existed: closes once, then never paints again. */
      if (visible && !pending) quietHide();
    } else if (ask && !visible && !justAnswered()) {
      /* Not painted (other market, or stk_cc says answered but Shopify forgot).
         A click recorded within FRESH is Shopify's one-page lag: stay closed. */
      mark(false);
      show();
    }
    /* Painted and unanswered stays up even where Shopify would not ask (a US
       visitor on the UK market): removing a card already on screen is the worse
       experience, and asking where it is not required is harmless. */

    if (pending) {
      var c = pending;
      pending = null;
      card.removeAttribute('aria-busy');
      record(c);
    }
  }

  function start() {
    wire();
    document.addEventListener('click', reopen);
    var opens = document.querySelectorAll('[data-cc-open]');
    for (var i = 0; i < opens.length; i++) opens[i].setAttribute('aria-haspopup', 'dialog');
    if (pre && !card.hidden && !root.classList.contains('cc-done')) {
      /* Same focus move show() makes, so a screen reader announces it; only if
         the visitor has not already put focus somewhere. */
      lastFocus = document.activeElement;
      if (!lastFocus || lastFocus === document.body) card.focus({ preventScroll: true });
    }
    /* Back button: a page restored from the browser cache keeps the card it
       had, even if the visitor answered on the page they came back from. */
    window.addEventListener('pageshow', function (e) {
      var flag = null;
      try { flag = localStorage.getItem('stk_cc'); } catch (x) {}
      if (e.persisted && flag && !card.hidden) {
        card.classList.remove('is-in');
        card.hidden = true;
      }
    });
    if (!window.Shopify || !window.Shopify.loadFeatures) return fail(true);
    timer = setTimeout(function () { fail(false); }, WAIT);
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        if (error) return fail(true);
        privacy = window.Shopify.customerPrivacy;
        if (!privacy || typeof privacy.shouldShowBanner !== 'function') {
          privacy = null;
          return fail(true);
        }
        try {
          ready();
        } catch (e) {
          privacy = null;
          fail(true);
        }
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
